import {useEffect, useRef} from 'react'

// Stack-based registry for global scanners. Only the topmost (most recently
// registered) scanner receives scan events, so overlays/drawers take priority
// over page-level scanners without needing extra coordination.
const globalScannerStack: symbol[] = []

function registerGlobalScanner(id: symbol) {
  globalScannerStack.push(id)
}

function unregisterGlobalScanner(id: symbol) {
  const idx = globalScannerStack.indexOf(id)
  if (idx !== -1) globalScannerStack.splice(idx, 1)
}

function isActiveGlobalScanner(id: symbol) {
  return globalScannerStack[globalScannerStack.length - 1] === id
}

interface UseScannerInputOptions {
  onComplete: (value: string) => void
  /**
   * If true, listens on window regardless of focus.
   */
  global?: boolean
}

export function useScanner(
  inputRef: React.RefObject<HTMLInputElement | null> | null,
  {onComplete, global = false}: UseScannerInputOptions
) {
  const bufferRef = useRef('')
  const isScanning = useRef(false)
  const scannerIdRef = useRef<symbol | null>(null)

  useEffect(() => {
    const target: EventTarget = global ? window : (inputRef?.current ?? window)

    if (global) {
      scannerIdRef.current = Symbol()
      registerGlobalScanner(scannerIdRef.current)
    }

    function handleKeyDown(e: KeyboardEvent) {
      // For global scanners, only the topmost registered scanner handles events
      if (global && scannerIdRef.current && !isActiveGlobalScanner(scannerIdRef.current)) return

      if (e.key === 'Enter') {
        e.preventDefault()
        e.stopPropagation()

        if (isScanning.current && bufferRef.current.length > 0) {
          try {
            onComplete(bufferRef.current)
          } finally {
            bufferRef.current = ''
            isScanning.current = false
          }
        }
        return
      }

      if (e.key === 'F8') {
        e.preventDefault()
        e.stopPropagation()
        isScanning.current = true
        bufferRef.current += '~'
        return
      }

      if (e.key.length === 1) {
        // ignore if user is typing in an input/textarea/select
        if (!global && document.activeElement !== inputRef?.current) return
        if (global && isUserTyping()) return

        if (!isScanning.current) {
          isScanning.current = true
          bufferRef.current = ''
        }
        bufferRef.current += e.key
      }
    }

    target.addEventListener('keydown', handleKeyDown as EventListener, true)
    return () => {
      target.removeEventListener('keydown', handleKeyDown as EventListener, true)
      if (global && scannerIdRef.current) {
        unregisterGlobalScanner(scannerIdRef.current)
      }
    }
  }, [inputRef, onComplete, global])
}

// prevents swallowing keystrokes when the user is typing in a form field
function isUserTyping(): boolean {
  const el = document.activeElement
  if (!el) return false
  const tag = el.tagName.toLowerCase()
  return tag === 'input' || tag === 'textarea' || tag === 'select' || (el as HTMLElement).isContentEditable
}
